/**
 * FleetClaims — Vue globale sinistres (Vague finale Parc Auto).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Trash2, FileSignature } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { collision: "Collision", theft: "Vol", vandalism: "Vandalisme",
                       fire: "Incendie", glass: "Bris de glace", other: "Autre" };
const RESP_LABEL = { ours: "Responsable", third_party: "Non-responsable",
                       shared: "Partagée", unknown: "Inconnue" };
const STATUS_STYLE = {
  open: { cls: "bg-red-100 text-red-700 border-red-200", label: "Ouvert" },
  in_repair: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "En réparation" },
  closed: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Clos" },
  archived: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Archivé" },
};

export default function FleetClaims() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("fleet.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [ocrLoading, setOcrLoading] = useState(false);

  const uploadConstat = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/fleet/claims/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const d = r.data;
      if (!d.matched_vehicle) {
        toast.error(`IA a détecté immat ${d.our_plate || "?"} mais aucun véhicule ne correspond — créez-le d'abord`);
        return;
      }
      if (!window.confirm(`Créer sinistre ?\n\nVéhicule: ${d.matched_vehicle.plate}\nDate: ${d.claim_date || "?"}\nType: ${d.claim_type || "?"}\nLieu: ${d.location || "?"}\nCoût estimé: ${d.estimated_cost || "?"} €\n\nDescription: ${(d.description || "").slice(0, 200)}`)) return;
      await api.post("/fleet/claims/apply", {
        vehicle_id: d.matched_vehicle.id,
        claim_date: d.claim_date,
        claim_type: d.claim_type || "collision",
        responsibility: d.responsibility || "unknown",
        location: d.location,
        description: d.description,
        third_party_info: [d.third_party_name, d.third_party_insurer, d.third_party_plate].filter(Boolean).join(" · "),
        estimated_cost: d.estimated_cost,
      });
      toast.success("Sinistre créé");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setOcrLoading(false); }
  };

  const load = async () => {
    try { const params = status !== "all" ? { status } : {};
      const r = await api.get("/fleet/claims", { params }); setRows(r.data.claims || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce sinistre ?")) return;
    try { await api.delete(`/fleet/claims/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const total = rows.reduce((s, r) => s + (r.estimated_cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="fleet-claims-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Sinistres</h1>
          <p className="text-sm text-slate-500">
            {rows.length} sinistre{rows.length > 1 ? "s" : ""} · coût total estimé {total.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
          </p>
        </div>
        {canWrite && (
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-purple-400 bg-purple-600 text-white hover:bg-purple-700 cursor-pointer text-sm font-medium" data-testid="ocr-claim-btn">
            {ocrLoading ? <FileSignature className="w-4 h-4 animate-pulse" /> : <FileSignature className="w-4 h-4" />}
            Importer constat (IA)
            <input type="file" accept="application/pdf,image/*" className="hidden"
                        onChange={e => uploadConstat(e.target.files?.[0])} />
          </label>
        )}
      </div>
      <Card><CardContent className="p-4 max-w-xs">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="claims-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="open">Ouverts</SelectItem>
            <SelectItem value="in_repair">En réparation</SelectItem>
            <SelectItem value="closed">Clos</SelectItem>
            <SelectItem value="archived">Archivés</SelectItem>
          </SelectContent>
        </Select>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun sinistre.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">Date</TableHead>
              <TableHead className="text-xs uppercase">Véhicule</TableHead>
              <TableHead className="text-xs uppercase">Type</TableHead>
              <TableHead className="text-xs uppercase">Responsabilité</TableHead>
              <TableHead className="text-xs uppercase">Description</TableHead>
              <TableHead className="text-xs uppercase text-right">Coût estimé</TableHead>
              <TableHead className="text-xs uppercase">Statut</TableHead>
              <TableHead className="text-xs uppercase text-right"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c, i) => {
                const st = STATUS_STYLE[c.status] || STATUS_STYLE.open;
                return (
                  <TableRow key={c.id} className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/app/fleet/vehicles/${c.vehicle_id}`)}
                    data-testid={`claim-row-${i}`}>
                    <TableCell className="font-mono text-sm">{new Date(c.claim_date).toLocaleDateString("fr-FR")}</TableCell>
                    <TableCell className="font-mono font-semibold">{c.plate}</TableCell>
                    <TableCell><Badge variant="outline">{TYPE_LABEL[c.claim_type]}</Badge></TableCell>
                    <TableCell><span className="text-sm">{RESP_LABEL[c.responsibility]}</span></TableCell>
                    <TableCell className="text-sm max-w-[280px] truncate" title={c.description}>{c.description || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{c.estimated_cost ? `${c.estimated_cost.toFixed(2)} €` : "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="text-red-600 h-7">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
